/**
 * LOV2B Home Activos 2.4
 * - Limits desktop filter views to 6 cards and loads more in batches of 6.
 * - Converts mobile grid into carousel with next button.
 * - Opens metaobject content in an accessible modal.
 * - Closes the modal on outside click, Escape, close button or when interacting outside.
 * - Shows a scroll cue when modal content is longer than the visible area.
 */
(function () {
  'use strict';

  function slugify(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' y ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function focusableElements(container) {
    return Array.prototype.slice.call(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  function init(root) {
    if (!root || root.dataset.lov2bHomeActivesPlus === 'true') return;
    root.dataset.lov2bHomeActivesPlus = 'true';

    var filters = Array.prototype.slice.call(root.querySelectorAll('[data-home-filter]'));
    var track = root.querySelector('[data-lov2b-home-actives-track]');
    var cards = track ? Array.prototype.slice.call(track.querySelectorAll('[data-lov2b-home-active-card]')) : [];
    var nav = root.querySelector('[data-lov2b-home-actives-nav]');
    var next = root.querySelector('[data-lov2b-home-actives-next]');
    var toggleWrap = root.querySelector('[data-lov2b-home-actives-toggle-wrap]');
    var toggle = root.querySelector('[data-lov2b-home-actives-toggle]');
    var modal = root.querySelector('[data-lov2b-home-actives-modal]');
    var modalBody = root.querySelector('[data-lov2b-home-actives-modal-body]');
    var closeTriggers = Array.prototype.slice.call(root.querySelectorAll('[data-lov2b-home-actives-close]'));
    var activeFilter = 'all';
    var desktopVisibleLimit = 6;
    var previousFocus = null;
    var suppressNextClick = false;
    var suppressTimer = null;

    function cardCategories(card) {
      var raw = String(card.dataset.homeCategories || '');
      return raw.split('|').map(slugify).filter(Boolean);
    }

    function cardMatches(card, filter) {
      if (filter === 'all') return true;
      return cardCategories(card).some(function (category) {
        return category === filter || category.indexOf(filter) !== -1 || filter.indexOf(category) !== -1;
      });
    }

    function matchingCards() {
      return cards.filter(function (card) { return cardMatches(card, activeFilter); });
    }

    function visibleCards() {
      return cards.filter(function (card) { return !card.hidden; });
    }

    function isMobileView() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    function updateNav() {
      if (!track || !nav || !next) return;
      var mobile = isMobileView();
      var canScroll = track.scrollWidth - track.clientWidth > 8;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
      var shouldHide = !mobile || visibleCards().length < 2 || !canScroll || atEnd;
      nav.classList.toggle('is-hidden', shouldHide);
      next.tabIndex = shouldHide ? -1 : 0;
    }

    function updateToggle(totalMatches) {
      if (!toggleWrap || !toggle) return;
      var shouldShow = !isMobileView() && totalMatches > 6;
      toggleWrap.hidden = !shouldShow;
      if (!shouldShow) {
        desktopVisibleLimit = 6;
      }
      var allVisible = shouldShow && desktopVisibleLimit >= totalMatches;
      toggle.setAttribute('aria-expanded', desktopVisibleLimit > 6 ? 'true' : 'false');
      toggle.textContent = allVisible
        ? (toggle.dataset.labelLess || 'Ver menos')
        : (toggle.dataset.labelMore || 'Ver más');
    }

    function applyFilter(filter, options) {
      var opts = options || {};
      if (opts.resetExpanded) desktopVisibleLimit = 6;
      activeFilter = slugify(filter || 'all') || 'all';

      var mobile = isMobileView();
      var matches = matchingCards();
      var limit = Math.min(desktopVisibleLimit, matches.length);
      cards.forEach(function (card) {
        var index = matches.indexOf(card);
        var shouldShow = index !== -1 && (mobile || index < limit);
        card.hidden = !shouldShow;
        card.classList.toggle('is-hidden', !shouldShow);
      });

      updateToggle(matches.length);
      if (track) track.scrollTo({ left: 0, behavior: 'auto' });
      window.setTimeout(updateNav, 0);
    }

    filters.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        filters.forEach(function (item) {
          var isCurrent = item === button;
          item.classList.toggle('is-active', isCurrent);
          item.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
        });
        applyFilter(button.dataset.homeFilter || 'all', { resetExpanded: true });
      }, true);
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        var totalMatches = matchingCards().length;
        if (desktopVisibleLimit >= totalMatches) {
          desktopVisibleLimit = 6;
        } else {
          desktopVisibleLimit = Math.min(desktopVisibleLimit + 6, totalMatches);
        }
        applyFilter(activeFilter);
      });
    }

    if (track) {
      track.addEventListener('scroll', updateNav, { passive: true });
      window.addEventListener('resize', function () {
        applyFilter(activeFilter);
        updateNav();
      });
    }

    if (next && track) {
      next.addEventListener('click', function () {
        var first = visibleCards()[0];
        var amount = first ? first.getBoundingClientRect().width + 12 : track.clientWidth * 0.76;
        track.scrollBy({ left: amount, behavior: 'smooth' });
      });
    }

    function updateModalScrollState() {
      if (!modal || !root.classList.contains('is-modal-open')) return;
      var scrollable = modal.scrollHeight - modal.clientHeight > 10;
      var atEnd = modal.scrollTop + modal.clientHeight >= modal.scrollHeight - 16;
      modal.classList.toggle('is-scrollable', scrollable);
      modal.classList.toggle('is-at-end', !scrollable || atEnd);
    }

    function closeModal() {
      if (!modal || !root.classList.contains('is-modal-open')) return;
      root.classList.remove('is-modal-open');
      modal.classList.remove('is-scrollable', 'is-at-end');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('lov2b-home-actives-lock');
      document.body.classList.remove('lov2b-home-actives-lock');
      if (modalBody) modalBody.innerHTML = '';
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      previousFocus = null;
    }

    function openModal(templateId, trigger) {
      if (!modal || !modalBody || !templateId) return;
      var template = document.getElementById(templateId);
      if (!template || !root.contains(template)) return;

      closeModal();
      previousFocus = trigger || document.activeElement;
      modalBody.innerHTML = template.innerHTML;
      modal.scrollTop = 0;
      var heading = modalBody.querySelector('h3');
      if (heading) {
        heading.id = modal.id + '-title';
        modal.setAttribute('aria-labelledby', heading.id);
      }
      root.classList.add('is-modal-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('lov2b-home-actives-lock');
      document.body.classList.add('lov2b-home-actives-lock');
      window.setTimeout(function () {
        updateModalScrollState();
        var first = focusableElements(modal)[0] || modal.querySelector('[data-lov2b-home-actives-close]');
        if (first) first.focus();
      }, 0);
    }

    cards.forEach(function (card) {
      var target = card.dataset.lov2bModalTarget;
      if (!target) return;
      card.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openModal(target, card);
      });
    });

    closeTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        closeModal();
      });
    });

    function armClickSuppression() {
      suppressNextClick = true;
      window.clearTimeout(suppressTimer);
      suppressTimer = window.setTimeout(function () { suppressNextClick = false; }, 450);
    }

    function closeWhenPointerOutside(event) {
      if (!modal || !root.classList.contains('is-modal-open')) return;
      if (modal.contains(event.target)) return;
      armClickSuppression();
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      closeModal();
    }

    function suppressClickAfterOutsideClose(event) {
      if (!suppressNextClick) return;
      suppressNextClick = false;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }

    document.addEventListener('pointerdown', closeWhenPointerOutside, true);
    document.addEventListener('click', suppressClickAfterOutsideClose, true);
    if (modal) modal.addEventListener('scroll', updateModalScrollState, { passive: true });
    window.addEventListener('resize', updateModalScrollState);

    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      if (event.key !== 'Tab' || !root.classList.contains('is-modal-open') || !modal) return;
      var focusables = focusableElements(modal);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    var initial = filters.filter(function (button) { return button.classList.contains('is-active'); })[0];
    applyFilter(initial ? initial.dataset.homeFilter : 'all', { resetExpanded: true });
  }

  function boot(scope) {
    var container = scope || document;
    if (container.matches && container.matches('[data-lov2b-home-ingredients]')) init(container);
    if (container.querySelectorAll) {
      Array.prototype.slice.call(container.querySelectorAll('[data-lov2b-home-ingredients]')).forEach(init);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
}());
